import * as React from "react";
import { useCallback, useState, useEffect } from "react";
import { useFormikContext } from "formik";
import styled from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import {
  ARCHIVE_RETENTION_STRATEGY,
  NOOP_RETENTION_STRATEGY,
  RETENTION,
  TIME_BASED_ROTATION_STRATEGY,
  TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY,
  TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY_TYPE
} from "stores/indices/IndicesStore";
import { Alert, Col, Input, Row } from "components/bootstrap";
import { Select } from "components/common";
import { useIndexRetention } from "components/indices/contexts/IndexRetentionContext";
const hasRetentionConfigField = (configData, field) => field in configData;
const StyledH3 = styled.h3`
  margin-bottom: 10px;
`;
const StyledSelect = styled(Select)`
  margin-bottom: 10px;
`;
const StyledAlert = styled(Alert)`
  overflow: auto;
`;
const getStrategyJsonSchema = (selectedStrategy, strategies) => {
  const result = strategies.filter((s) => s.type === selectedStrategy)[0];
  return result ? result.json_schema : void 0;
};
const getDefaultStrategyConfig = (selectedStrategy, strategies) => {
  const result = strategies.filter((s) => s.type === selectedStrategy)[0];
  return result ? result.default_config : void 0;
};
const getTimeBaseStrategyWithElasticLimit = (activeConfig, strategies) => {
  const timeBasedStrategy = getDefaultStrategyConfig(
    TIME_BASED_ROTATION_STRATEGY,
    strategies
  );
  return { ...activeConfig, max_rotation_period: timeBasedStrategy == null ? void 0 : timeBasedStrategy.max_rotation_period };
};
const getStrategyConfig = (configTypeName, selectedStrategy, activeStrategy, activeConfig, strategies) => {
  if (selectedStrategy === TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY && configTypeName === "retention") {
    return activeConfig;
  }
  if (activeStrategy === selectedStrategy) {
    return activeStrategy === TIME_BASED_ROTATION_STRATEGY ? getTimeBaseStrategyWithElasticLimit(activeConfig, strategies) : activeConfig;
  }
  return getDefaultStrategyConfig(selectedStrategy, strategies);
};
const getConfigurationComponent = (configTypeName, selectedStrategy, pluginExports, strategies, strategy, config, onConfigUpdate, useMaxNumberOfIndices, disabled = false) => {
  if (!selectedStrategy || selectedStrategy.length < 1) {
    return null;
  }
  const strategyPlugin = pluginExports.filter((exportedStrategy) => exportedStrategy.type === selectedStrategy)[0];
  if (!strategyPlugin) {
    return null;
  }
  const strategyType = typeof strategy === "string" ? strategy : strategy == null ? void 0 : strategy.type;
  const strategyConfig = getStrategyConfig(configTypeName, selectedStrategy, strategyType, config, strategies);
  let componentProps = {
    config: strategyConfig,
    jsonSchema: getStrategyJsonSchema(selectedStrategy, strategies),
    updateConfig: onConfigUpdate,
    disabled
  };
  if (selectedStrategy === ARCHIVE_RETENTION_STRATEGY) {
    componentProps = { ...componentProps, useMaxNumberOfIndices };
  }
  const element = React.createElement(
    strategyPlugin.configComponent,
    componentProps
  );
  return /* @__PURE__ */ React.createElement("div", { key: strategyType }, element);
};
const IndexMaintenanceStrategiesConfiguration = ({
  title,
  name,
  description = void 0,
  selectPlaceholder,
  label,
  pluginExports,
  strategies,
  immutableFields = [],
  disabled = false,
  retentionStrategiesContext: { max_index_retention_period: maxRetentionPeriod } = {
    max_index_retention_period: void 0
  },
  activeConfig: { strategy, config },
  getState
}) => {
  const [newStrategy, setNewStrategy] = useState(strategy);
  const {
    setValues,
    values,
    values: {
      rotation_strategy: rotationStrategy,
      rotation_strategy_class: rotationStrategyClass,
      retention_strategy_class: retentionStrategyClass
    },
    errors
  } = useFormikContext();
  const [maxNumberOfIndices, setMaxNumberOfIndices] = useIndexRetention().useMaxNumberOfIndices;
  useEffect(() => {
    if (config && hasRetentionConfigField(config, "max_number_of_indices")) {
      setMaxNumberOfIndices(config.max_number_of_indices);
    }
  }, [config, setMaxNumberOfIndices]);
  const retentionIsNotNoop = retentionStrategyClass !== NOOP_RETENTION_STRATEGY;
  const isArchiveRetention = retentionStrategyClass !== ARCHIVE_RETENTION_STRATEGY;
  const shouldShowMaxRetentionWarning = maxRetentionPeriod && rotationStrategyClass === TIME_BASED_ROTATION_STRATEGY && retentionIsNotNoop;
  const isTimeBasedSizeOptimizing = rotationStrategyClass === TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY;
  const shouldShowTimeBasedSizeOptimizingForm = isTimeBasedSizeOptimizing && name === "retention" && retentionIsNotNoop;
  const shouldShowNormalRetentionForm = !isTimeBasedSizeOptimizing || name === "retention" && (!retentionIsNotNoop || !isArchiveRetention);
  const helpText = isTimeBasedSizeOptimizing && name === "rotation" ? "The Time Based Size Optimizing Rotation Strategy tries to rotate the index daily. It can however skip the rotation to achieve optimal sized indices by keeping the shard size within an acceptable range. The optimization can delay the rotation within the range of the configured retention min/max lifetime. If an index is older than the range between min/max, it will be rotated regardless of its current size." : null;
  const _onSelect = (selectedStrategy) => {
    if (!selectedStrategy || selectedStrategy.length < 1) {
      setNewStrategy(void 0);
      return;
    }
    const newConfig = getStrategyConfig(name, selectedStrategy, strategy, config, strategies);
    setNewStrategy(selectedStrategy);
    setValues({ ...values, ...getState(selectedStrategy, newConfig) });
  };
  const _onConfigUpdate = useCallback(
    (newConfig) => {
      const _addConfigType = (selectedStrategy, data) => {
        const result = strategies.filter((s) => s.type === selectedStrategy)[0];
        const copy = data;
        if (result) {
          copy.type = result.default_config.type;
        }
        return copy;
      };
      const configuration = _addConfigType(newStrategy, newConfig);
      setValues({ ...values, ...getState(newStrategy, configuration) });
    },
    [getState, newStrategy, setValues, strategies, values]
  );
  const _onIndexTimeSizeOptimizingUpdate = useCallback(
    (newConfig) => {
      if (isTimeBasedSizeOptimizing) {
        setValues({
          ...values,
          ...{
            rotation_strategy_class: rotationStrategyClass,
            rotation_strategy: { ...newConfig, type: TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY_TYPE }
          }
        });
      }
    },
    [isTimeBasedSizeOptimizing, rotationStrategyClass, setValues, values]
  );
  const getAvailableSelectOptions = () => {
    const availableStrategies = pluginExports.filter(
      (pluginOptions) => strategies.find(({ type }) => type === pluginOptions.type)
    );
    const isSelectedItemInList = availableStrategies.filter((availableStrategy) => availableStrategy.type === newStrategy).length > 0;
    if (!isSelectedItemInList) {
      const selectedItemStrategy = pluginExports.find((pluginOptions) => pluginOptions.type === newStrategy);
      if (selectedItemStrategy) {
        return [...availableStrategies, selectedItemStrategy].map((pluginOptions) => ({
          value: pluginOptions.type,
          label: pluginOptions.displayName
        }));
      }
      return availableStrategies.map((pluginOptions) => ({
        value: pluginOptions.type,
        label: pluginOptions.displayName
      }));
    }
    return availableStrategies.map((c) => ({ value: c.type, label: c.displayName }));
  };
  const getDisplayName = () => pluginExports.find((pluginOptions) => pluginOptions.type === newStrategy).displayName;
  const getActiveSelection = () => newStrategy;
  const shouldShowInvalidRetentionWarning = () => !!newStrategy && name === RETENTION && !getStrategyJsonSchema(getActiveSelection(), strategies);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(StyledH3, null, title), description && /* @__PURE__ */ React.createElement(StyledAlert, null, description), helpText && /* @__PURE__ */ React.createElement(StyledAlert, null, helpText), shouldShowMaxRetentionWarning && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, "The effective retention period value calculated from the", /* @__PURE__ */ React.createElement("b", null, "Rotation period"), " and the ", /* @__PURE__ */ React.createElement("b", null, "max number of indices"), " should not be greater than the", /* @__PURE__ */ React.createElement("b", null, "Max retention period "), " of ", /* @__PURE__ */ React.createElement("b", null, maxRetentionPeriod), " set by the Administrator."), shouldShowInvalidRetentionWarning() && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, getDisplayName(), " strategy was deactivated. Please configure a valid retention strategy."), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "strategy-select",
      error: errors[`${name}_strategy_class`],
      name: `${name}_strategy_class`,
      label
    },
    /* @__PURE__ */ React.createElement(
      StyledSelect,
      {
        placeholder: selectPlaceholder,
        options: getAvailableSelectOptions(),
        value: getActiveSelection(),
        onChange: _onSelect,
        clearable: false,
        disabled
      }
    )
  ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, shouldShowTimeBasedSizeOptimizingForm && getConfigurationComponent(
    name,
    TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY,
    PluginStore.exports("indexRotationConfig"),
    [rotationStrategy],
    rotationStrategy,
    rotationStrategy,
    _onIndexTimeSizeOptimizingUpdate,
    () => [maxNumberOfIndices, setMaxNumberOfIndices],
    immutableFields.includes("legacy.rotation_strategy")
  ), shouldShowNormalRetentionForm && getConfigurationComponent(
    name,
    getActiveSelection(),
    pluginExports,
    strategies,
    strategy,
    config,
    _onConfigUpdate,
    () => [maxNumberOfIndices, setMaxNumberOfIndices],
    disabled
  ))));
};
export default IndexMaintenanceStrategiesConfiguration;
